import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Eye, EyeOff, LogIn, Mail } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // Password recovery dialog state
  const [recoveryMode, setRecoveryMode] = useState(null); // "forgot" | "first-time" | null
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoverySubmitting, setRecoverySubmitting] = useState(false);

  const handleRecoverySubmit = async (e) => {
    e.preventDefault();
    setRecoverySubmitting(true);
    try {
      const endpoint = recoveryMode === "forgot" ? "forgot-password" : "first-time-password";
      const { data } = await axios.post(`${API}/auth/${endpoint}`, { email: recoveryEmail });
      toast.success(data.message || "Check your email for instructions.");
      setRecoveryMode(null);
      setRecoveryEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setRecoverySubmitting(false);
    }
  };

  useEffect(() => {
    // Initialize admin on first load
    const initAdmin = async () => {
      try {
        await axios.post(`${API}/init-admin`);
      } catch (error) {
        // Ignore errors - admin might already exist
      }
    };
    initAdmin();
  }, []);

  useEffect(() => {
    if (user) {
      navigate(`/${user.role}`, { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const userData = await login(email, password);
      toast.success(`Welcome back, ${userData.name}!`);
      navigate(`/${userData.role}`, { replace: true });
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed. Please try again.";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          {/* Logo & Header */}
          <div className="text-center">
            <div className="flex justify-center mb-8">
              <img 
                src="/roofnuts-logo.png" 
                alt="RoofNuts Logo" 
                className="w-56 h-56 object-contain rounded-xl"
              />
            </div>
            <h2 className="text-3xl font-bold text-foreground font-['Chivo'] tracking-tight">
              Sign in to your account
            </h2>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground font-medium">
                Email Address
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                data-testid="email-input"
                className="h-12 bg-input/50 border-input focus:border-primary rounded-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground font-medium">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  data-testid="password-input"
                  className="h-12 bg-input/50 border-input focus:border-primary rounded-sm pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              data-testid="login-button"
              className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wide rounded-sm"
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground"></div>
                  Signing in...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <LogIn size={18} />
                  Sign In
                </div>
              )}
            </Button>
          </form>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-sm">
            <button
              type="button"
              onClick={() => { setRecoveryMode("forgot"); setRecoveryEmail(email); }}
              className="text-primary hover:underline font-medium"
            >
              Forgot your password?
            </button>
            <span className="hidden sm:inline text-muted-foreground">•</span>
            <button
              type="button"
              onClick={() => { setRecoveryMode("first-time"); setRecoveryEmail(email); }}
              className="text-primary hover:underline font-medium"
            >
              First time logging in?
            </button>
          </div>

          <p className="text-center text-sm text-muted-foreground">
            Contact your administrator if you need an account
          </p>

          <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground pt-2">
            <a href="/eula" className="hover:underline">EULA</a>
            <span>·</span>
            <a href="/privacy" className="hover:underline">Privacy Policy</a>
          </div>

          <p className="text-center text-xs text-muted-foreground">
            Website by{" "}
            <a
              href="https://parker-local.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
              Parker Local Web Design
            </a>
          </p>
        </div>
      </div>

      <Dialog open={recoveryMode !== null} onOpenChange={(open) => { if (!open) { setRecoveryMode(null); setRecoveryEmail(""); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {recoveryMode === "forgot" ? "Reset Your Password" : "Request Your Initial Password"}
            </DialogTitle>
            <DialogDescription>
              {recoveryMode === "forgot"
                ? "Enter the email address on your account. We'll send you a new temporary password."
                : "Enter the email address on your account. We'll email you the password to sign in for the first time."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRecoverySubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="recovery-email">Email Address</Label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <Input
                  id="recovery-email"
                  type="email"
                  value={recoveryEmail}
                  onChange={(e) => setRecoveryEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                  className="pl-9"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRecoveryMode(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={recoverySubmitting || !recoveryEmail}>
                {recoverySubmitting ? "Sending..." : "Send Email"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Right side - Hero Image */}
      <div 
        className="hidden lg:block lg:w-1/2 relative"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1722619734320-9daad59bac19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxyb29maW5nJTIwY29udHJhY3RvciUyMHN1bnNldHxlbnwwfHx8fDE3NjYwODkyMzF8MA&ixlib=rb-4.1.0&q=85)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to right, rgba(17, 24, 39, 0.95), rgba(17, 24, 39, 0.4))'
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-left max-w-lg animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <h2 className="text-4xl font-bold text-white font-['Chivo'] tracking-tight mb-4">
              #1 Marketing Partner Of Top Roofing Contractors
            </h2>
            <p className="text-lg text-gray-300">
              Manage your cold calling campaigns, track leads, and maximize your team's performance with our comprehensive portal.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
